import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Sparkles, X } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from '../services/api';

const QUICK_FINDINGS = ['Cavity', 'Fractured', 'Removed', 'Chipped'];
const ISSUE_VERSION = 'dektal.chart.v2';

const ADULT_CHART = {
  upper: ['1/8', '1/7', '1/6', '1/5', '1/4', '1/3', '1/2', '1/1', '2/1', '2/2', '2/3', '2/4', '2/5', '2/6', '2/7', '2/8'],
  lower: ['4/8', '4/7', '4/6', '4/5', '4/4', '4/3', '4/2', '4/1', '3/1', '3/2', '3/3', '3/4', '3/5', '3/6', '3/7', '3/8'],
};

const PEDIATRIC_CHART = {
  upper: ['5/5', '5/4', '5/3', '5/2', '5/1', '6/1', '6/2', '6/3', '6/4', '6/5'],
  lower: ['8/5', '8/4', '8/3', '8/2', '8/1', '7/1', '7/2', '7/3', '7/4', '7/5'],
};

const ARCH_OFFSETS = {
  adult: {
    upper: [34, 24, 16, 9, 4, 1, 0, 3, 3, 0, 1, 4, 9, 16, 24, 34],
    lower: [24, 16, 9, 4, 1, 0, 0, 3, 3, 0, 0, 1, 4, 9, 16, 24],
  },
  pediatric: {
    upper: [24, 15, 8, 3, 0, 0, 3, 8, 15, 24],
    lower: [18, 10, 4, 1, 0, 0, 1, 4, 10, 18],
  },
};

const TOOTH_SEQUENCE = [
  ...ADULT_CHART.upper,
  ...ADULT_CHART.lower,
  ...PEDIATRIC_CHART.upper,
  ...PEDIATRIC_CHART.lower,
];

const TOOTH_ORDER = new Map(
  TOOTH_SEQUENCE.map((toothNumber, index) => [String(toothNumber), index])
);

function createEntryMap(rows) {
  return rows.map((toothNumber) => ({
    toothNumber: String(toothNumber),
    label: String(toothNumber),
  }));
}

function getQuadrantHints(mode, orientation) {
  if (mode === 'pediatric') {
    return orientation === 'upper'
      ? ['Upper Right Child Quadrant', 'Upper Left Child Quadrant']
      : ['Lower Right Child Quadrant', 'Lower Left Child Quadrant'];
  }

  return orientation === 'upper'
    ? ['Upper Right Quadrant', 'Upper Left Quadrant']
    : ['Lower Right Quadrant', 'Lower Left Quadrant'];
}

const CHART_CONFIG = {
  adult: {
    upper: createEntryMap(ADULT_CHART.upper),
    lower: createEntryMap(ADULT_CHART.lower),
  },
  pediatric: {
    upper: createEntryMap(PEDIATRIC_CHART.upper),
    lower: createEntryMap(PEDIATRIC_CHART.lower),
  },
};

function sanitizeFindings(findings) {
  const unique = [];
  (Array.isArray(findings) ? findings : []).forEach((finding) => {
    const normalized = QUICK_FINDINGS.find(
      (option) => option.toLowerCase() === String(finding || '').toLowerCase()
    );

    if (normalized && !unique.includes(normalized)) {
      unique.push(normalized);
    }
  });

  return unique;
}

function createEmptyRecord(mode = 'adult') {
  return {
    findings: [],
    note: '',
    mode,
    recordId: null,
  };
}

function parseLegacyText(issue, fallbackMode) {
  const rawText = String(issue || '').trim();
  if (!rawText) {
    return createEmptyRecord(fallbackMode);
  }

  const tokens = rawText
    .split(/[,\n;/]+/)
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean);

  const matchedFindings = [];

  tokens.forEach((token) => {
    if (['cavity', 'caries'].includes(token) && !matchedFindings.includes('Cavity')) {
      matchedFindings.push('Cavity');
    }

    if (['fracture', 'fractured'].includes(token) && !matchedFindings.includes('Fractured')) {
      matchedFindings.push('Fractured');
    }

    if (['removed', 'missing'].includes(token) && !matchedFindings.includes('Removed')) {
      matchedFindings.push('Removed');
    }

    if (['chip', 'chipped'].includes(token) && !matchedFindings.includes('Chipped')) {
      matchedFindings.push('Chipped');
    }
  });

  const isPureQuickFindings =
    tokens.length > 0 &&
    tokens.every((token) =>
      ['cavity', 'caries', 'fracture', 'fractured', 'removed', 'missing', 'chip', 'chipped'].includes(token)
    );

  return {
    findings: matchedFindings,
    note: isPureQuickFindings ? '' : rawText,
    mode: fallbackMode,
    recordId: null,
  };
}

function parseIssue(issue, fallbackMode) {
  if (!issue) {
    return createEmptyRecord(fallbackMode);
  }

  try {
    const parsed = JSON.parse(issue);

    if (parsed && typeof parsed === 'object') {
      return {
        findings: sanitizeFindings(parsed.findings),
        note: typeof parsed.note === 'string' ? parsed.note : '',
        mode: parsed.mode === 'pediatric' ? 'pediatric' : fallbackMode,
        recordId: null,
      };
    }
  } catch (_error) {
    return parseLegacyText(issue, fallbackMode);
  }

  return parseLegacyText(issue, fallbackMode);
}

function serializeIssue(record) {
  return JSON.stringify({
    version: ISSUE_VERSION,
    mode: record.mode === 'pediatric' ? 'pediatric' : 'adult',
    findings: sanitizeFindings(record.findings),
    note: String(record.note || ''),
  });
}

function hasContent(record) {
  return Boolean(record?.findings?.length || String(record?.note || '').trim());
}

function normalizeEntry(entry) {
  const mode = entry?.quadrant === 'pediatric' ? 'pediatric' : 'adult';
  const parsed = parseIssue(entry?.issue, mode);

  return {
    recordId: entry?.id || null,
    findings: parsed.findings,
    note: parsed.note,
    mode,
  };
}

function buildChartSummary(chartData) {
  return Object.entries(chartData)
    .filter(([, record]) => hasContent(record))
    .sort(([left], [right]) => {
      const leftOrder = TOOTH_ORDER.get(String(left)) ?? Number.MAX_SAFE_INTEGER;
      const rightOrder = TOOTH_ORDER.get(String(right)) ?? Number.MAX_SAFE_INTEGER;
      return leftOrder - rightOrder;
    })
    .map(([toothNumber, record]) => {
      const detailParts = [];

      if (record.findings.length) {
        detailParts.push(record.findings.join(', '));
      }

      if (String(record.note || '').trim()) {
        detailParts.push(`Note: ${record.note.trim()}`);
      }

      return `- Tooth ${toothNumber}: ${detailParts.join('. ')}`;
    })
    .join('\n');
}

function ChartArch({
  teeth,
  offsets,
  orientation,
  selectedToothKey,
  chartData,
  onSelectTooth,
  title,
  sideHint,
}) {
  const isUpper = orientation === 'upper';
  const midlineIndex = Math.floor(teeth.length / 2) - 1;
  const leftGroupCount = midlineIndex + 1;
  const rightGroupCount = teeth.length - leftGroupCount;
  const titleGridStyle = {
    gridTemplateColumns: `minmax(0, ${leftGroupCount}fr) auto minmax(0, ${rightGroupCount}fr)`,
  };

  return (
    <div
      className={`relative overflow-visible border border-rose-100 bg-gradient-to-b ${
        isUpper ? 'from-white via-rose-50 to-rose-100/80' : 'from-rose-50 via-rose-100/85 to-rose-200/80'
      } ${
        isUpper ? 'rounded-[38px_38px_24px_24px]' : 'rounded-[24px_24px_38px_38px]'
      }`}
    >
      <div className="overflow-x-auto px-3 py-7 sm:px-5" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-x' }}>
        <div className="mx-auto flex w-max min-w-max flex-col py-3">
          <div
            className="grid w-full items-center gap-3 pb-5 text-[9px] font-semibold uppercase leading-tight tracking-[0.12em] text-rose-300 sm:text-[10px] sm:tracking-[0.18em]"
            style={titleGridStyle}
          >
            <span className="text-center">{sideHint[0]}</span>
            <span className="text-center">{title}</span>
            <span className="text-center">{sideHint[1]}</span>
          </div>

          <div
            className={`relative z-10 flex justify-center gap-1 sm:gap-2 ${
              isUpper ? 'items-end' : 'items-start'
            }`}
          >
            {teeth.map((tooth, index) => {
              const toothKey = tooth.toothNumber;
              const isSelected = selectedToothKey === toothKey;
              const toothState = chartData[toothKey];
              const isMarked = hasContent(toothState);
              const style = isUpper ? { marginTop: `${offsets[index]}px` } : { marginBottom: `${offsets[index]}px` };

              return (
                <React.Fragment key={toothKey}>
                <button
                  type="button"
                  onClick={() => onSelectTooth(tooth)}
                  style={style}
                  className="group flex min-w-[34px] flex-col items-center focus:outline-none sm:min-w-[38px]"
                >
                  {isUpper && (
                    <span
                      className={`mb-2 text-[10px] font-semibold tracking-normal transition-colors ${
                        isSelected ? 'text-indigo-700' : 'text-slate-500'
                      }`}
                    >
                      {tooth.label}
                    </span>
                  )}

                  <span
                    className={`relative block h-9 w-7 rounded-[16px_16px_11px_11px] border bg-gradient-to-b shadow-[0_10px_18px_-16px_rgba(15,23,42,0.4)] transition-all sm:h-10 sm:w-8 ${
                      isSelected
                        ? 'border-indigo-400 from-white to-indigo-50 shadow-[0_12px_24px_-18px_rgba(37,99,235,0.48)] ring-4 ring-indigo-100/80'
                        : isMarked
                        ? 'border-rose-300 from-white to-rose-50 shadow-[0_12px_24px_-20px_rgba(244,63,94,0.36)]'
                        : 'border-white/90 from-white to-slate-100 group-hover:border-indigo-200 group-hover:to-indigo-50'
                    }`}
                  >
                    <span
                      className={`absolute left-1/2 top-1.5 h-3.5 w-4.5 -translate-x-1/2 rounded-full border border-white/80 ${
                        isMarked ? 'bg-rose-200' : 'bg-slate-100'
                      }`}
                    />
                    {isMarked && (
                      <span className="absolute right-1 top-1 h-2.5 w-2.5 rounded-full border border-white bg-rose-500" />
                    )}
                  </span>

                  {!isUpper && (
                    <span
                      className={`mt-2 text-[10px] font-semibold tracking-normal transition-colors ${
                        isSelected ? 'text-indigo-700' : 'text-slate-500'
                      }`}
                    >
                      {tooth.label}
                    </span>
                  )}
                </button>
                {index === midlineIndex ? (
                  <div className="mx-1 h-20 w-px self-center rounded-full bg-rose-200/90" aria-hidden="true" />
                ) : null}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DentalChart({ patientId, onSummaryChange }) {
  const [mode, setMode] = useState('adult');
  const [chartData, setChartData] = useState({});
  const [selectedToothKey, setSelectedToothKey] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [hydrated, setHydrated] = useState(false);
  const [savingToothKey, setSavingToothKey] = useState(null);

  const currentChart = mode === 'adult' ? CHART_CONFIG.adult : CHART_CONFIG.pediatric;
  const currentOffsets = mode === 'adult' ? ARCH_OFFSETS.adult : ARCH_OFFSETS.pediatric;

  const toothLookup = useMemo(() => {
    const allTeeth = [...currentChart.upper, ...currentChart.lower];
    return new Map(allTeeth.map((tooth) => [tooth.toothNumber, tooth]));
  }, [currentChart]);

  const selectedTooth = selectedToothKey ? toothLookup.get(selectedToothKey) || null : null;
  const selectedRecord = selectedToothKey
    ? chartData[selectedToothKey] || createEmptyRecord(mode)
    : createEmptyRecord(mode);

  const chartSummary = useMemo(() => buildChartSummary(chartData), [chartData]);
  const filledToothCount = useMemo(
    () => Object.values(chartData).filter((record) => hasContent(record)).length,
    [chartData]
  );

  useEffect(() => {
    let isActive = true;
    setHydrated(false);

    if (!patientId) {
      setChartData({});
      setSelectedToothKey(null);
      setHydrated(true);
      return undefined;
    }

    axios
      .get(`/dental-chart/${patientId}`)
      .then((res) => {
        if (!isActive) return;

        const nextChartData = {};

        (res.data || []).forEach((entry) => {
          nextChartData[String(entry.toothNumber)] = normalizeEntry(entry);
        });

        setChartData(nextChartData);

        const firstMarkedTooth = Object.keys(nextChartData).find((toothNumber) => hasContent(nextChartData[toothNumber]));
        if (firstMarkedTooth) {
          setSelectedToothKey(firstMarkedTooth);
        }
      })
      .catch((error) => {
        console.error('Failed to load dental chart', error);
        toast.error('Failed to load dental chart');
      })
      .finally(() => {
        if (isActive) {
          setHydrated(true);
        }
      });

    return () => {
      isActive = false;
    };
  }, [patientId]);

  useEffect(() => {
    if (!hydrated) return;
    onSummaryChange?.(chartSummary);
  }, [chartSummary, hydrated, onSummaryChange]);

  useEffect(() => {
    if (!selectedToothKey) {
      setNoteDraft('');
      return;
    }

    setNoteDraft(selectedRecord.note || '');
  }, [selectedToothKey, selectedRecord.note]);

  useEffect(() => {
    if (selectedToothKey && !toothLookup.has(selectedToothKey)) {
      setSelectedToothKey(null);
    }
  }, [mode, selectedToothKey, toothLookup]);

  const persistToothRecord = async (tooth, nextRecord) => {
    if (!patientId || !tooth) {
      return;
    }

    const toothKey = tooth.toothNumber;
    const existingRecordId = chartData[toothKey]?.recordId;
    setSavingToothKey(toothKey);

    try {
      if (!hasContent(nextRecord)) {
        if (existingRecordId) {
          await axios.delete(`/dental-chart/${existingRecordId}`);
        }

        setChartData((prev) => {
          const next = { ...prev };
          delete next[toothKey];
          return next;
        });
        return;
      }

      const payload = {
        patientId,
        toothNumber: toothKey,
        issue: serializeIssue(nextRecord),
        quadrant: nextRecord.mode === 'pediatric' ? 'pediatric' : 'adult',
      };

      const res = await axios.post('/dental-chart', payload);
      setChartData((prev) => ({
        ...prev,
        [toothKey]: normalizeEntry(res.data),
      }));
    } catch (error) {
      console.error('Failed to save tooth details', error);
      toast.error(`Failed to save details for Tooth ${toothKey}`);
    } finally {
      setSavingToothKey((current) => (current === toothKey ? null : current));
    }
  };

  const persistSelectedNote = async (overrideTooth = selectedTooth) => {
    if (!overrideTooth) return;

    const toothKey = overrideTooth.toothNumber;
    const currentRecord = chartData[toothKey] || createEmptyRecord(mode);
    const trimmedDraft = noteDraft.trim();
    const trimmedSaved = String(currentRecord.note || '').trim();

    if (trimmedDraft === trimmedSaved) {
      return;
    }

    await persistToothRecord(overrideTooth, {
      ...currentRecord,
      note: noteDraft,
      mode,
    });
  };

  const handleSelectTooth = async (tooth) => {
    if (selectedToothKey && selectedToothKey !== tooth.toothNumber) {
      await persistSelectedNote();
    }

    setSelectedToothKey(tooth.toothNumber);
  };

  const toggleFinding = async (finding) => {
    if (!selectedTooth) return;

    const currentFindings = selectedRecord.findings || [];
    const nextFindings = currentFindings.includes(finding)
      ? currentFindings.filter((item) => item !== finding)
      : [...currentFindings, finding];

    const nextRecord = {
      ...selectedRecord,
      findings: nextFindings,
      note: noteDraft,
      mode,
    };

    setChartData((prev) => ({
      ...prev,
      [selectedTooth.toothNumber]: nextRecord,
    }));

    await persistToothRecord(selectedTooth, nextRecord);
  };

  const clearSelectedTooth = async () => {
    if (!selectedTooth) return;

    setNoteDraft('');
    await persistToothRecord(selectedTooth, createEmptyRecord(mode));
  };

  return (
    <div className="rounded-[22px] border border-slate-100 bg-[#FCFDFF] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)] sm:p-5">
      <div className="mb-5 flex flex-col gap-4 border-b border-slate-100 pb-4 lg:flex-row lg:items-center lg:justify-end">
        <div className="flex items-center gap-3">
          <div className="rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-indigo-700">
            {filledToothCount} Teeth Marked
          </div>
          <div className="flex rounded-full border border-slate-200 bg-white p-1 shadow-[0_10px_24px_-22px_rgba(15,23,42,0.4)]">
            <button
              type="button"
              onClick={() => setMode('adult')}
              className={`rounded-full px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] transition-all ${
                mode === 'adult'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Adult
            </button>
            <button
              type="button"
              onClick={() => setMode('pediatric')}
              className={`rounded-full px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] transition-all ${
                mode === 'pediatric'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Child
            </button>
          </div>
        </div>
      </div>

      <div className="grid min-w-0 max-w-full gap-5 xl:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0 max-w-full space-y-6">
          <ChartArch
            teeth={currentChart.upper}
            offsets={currentOffsets.upper}
            orientation="upper"
            selectedToothKey={selectedToothKey}
            chartData={chartData}
            onSelectTooth={handleSelectTooth}
            title={mode === 'adult' ? 'Upper Jaw' : 'Upper Arch'}
            sideHint={getQuadrantHints(mode, 'upper')}
          />

          <ChartArch
            teeth={currentChart.lower}
            offsets={currentOffsets.lower}
            orientation="lower"
            selectedToothKey={selectedToothKey}
            chartData={chartData}
            onSelectTooth={handleSelectTooth}
            title={mode === 'adult' ? 'Lower Jaw' : 'Lower Arch'}
            sideHint={getQuadrantHints(mode, 'lower')}
          />
        </div>

        <div className="rounded-[22px] border border-slate-200 bg-white p-5 shadow-[0_18px_38px_-34px_rgba(15,23,42,0.24)]">
          {selectedTooth ? (
            <>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                    Tooth Interaction
                  </p>
                  <h3 className="mt-2 text-[22px] font-semibold tracking-[-0.01em] text-slate-900">Tooth {selectedTooth.toothNumber}</h3>
                </div>

                <button
                  type="button"
                  onClick={async () => {
                    await persistSelectedNote();
                    setSelectedToothKey(null);
                  }}
                  className="rounded-full border border-slate-200 p-2 text-slate-400 transition-colors hover:border-slate-300 hover:text-slate-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                  Quick Findings
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {QUICK_FINDINGS.map((finding) => {
                    const isActive = selectedRecord.findings.includes(finding);
                    return (
                      <button
                        key={finding}
                        type="button"
                        onClick={() => toggleFinding(finding)}
                        className={`rounded-full border px-3.5 py-2 text-[12px] font-medium transition-all ${
                          isActive
                            ? 'border-indigo-600 bg-indigo-600 text-white shadow-[0_10px_18px_-16px_rgba(15,23,42,0.45)]'
                            : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700'
                        }`}
                      >
                        {finding}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-5">
                <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                  Tooth-Specific Notes
                </label>
                <textarea
                  value={noteDraft}
                  onChange={(event) => setNoteDraft(event.target.value)}
                  onBlur={() => persistSelectedNote()}
                  placeholder="RCT done previously, X-ray needed, tenderness, mobility..."
                  className="mt-2.5 min-h-[128px] w-full rounded-[18px] border border-slate-200 bg-slate-50/80 px-3.5 py-3.5 text-[13px] font-medium text-slate-700 outline-none transition-all focus:border-indigo-300 focus:bg-white focus:ring-4 focus:ring-indigo-50"
                />
              </div>

              <div className="mt-4 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={clearSelectedTooth}
                  className="rounded-full border border-rose-200 bg-rose-50 px-3.5 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-rose-700 transition-colors hover:bg-rose-100"
                >
                  Clear Tooth
                </button>

                <div className="flex items-center gap-2 text-[11px] font-medium text-slate-500">
                  {savingToothKey === selectedTooth.toothNumber ? (
                    <>
                      <Sparkles className="h-4 w-4 text-indigo-500" />
                      Saving details...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 text-indigo-500" />
                      Changes save automatically
                    </>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="flex h-full flex-col items-center justify-center rounded-[20px] border border-dashed border-slate-200 bg-slate-50 px-5 py-10 text-center">
              <div className="rounded-full bg-indigo-50 p-3.5 text-indigo-600">
                <AlertCircle className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-[18px] font-semibold tracking-[-0.01em] text-slate-900">Select a tooth</h3>
              <p className="mt-2 max-w-xs text-[13px] font-medium text-slate-500">
                Choose any tooth from the chart to mark quick findings and add observation notes.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
